import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Chip,
  IconButton,
  LinearProgress,
  Alert,
  Paper,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { useChains } from 'wagmi'
import { Chain, zeroAddress } from 'viem'
import useNewSafe from '../hooks/useNewSafe'
import {
  isValidAddress,
  sanitizeUserInput,
  getRandomSafeName,
} from '../utils/helpers'
import { STEPS_DEPLOY_LABEL, DEFAULT_DEPLOY_STEPS } from '../utils/constants'
import { SafeDeployStep } from '../utils/types'
import { useTheme } from '@/contexts/ThemeContext'
import { useNotification } from '@/hooks/useNotification'
import { isUserRejectedError } from '@/utils/errorHandler'
import TransactionCancelledModal from '@/components/TransactionCancelledModal'
import { getEnv } from '@/config/env'
import { useWallet } from '@/hooks/useWallet'

const MIN_SAFE_THRESHOLD = 2

interface CreateSafeModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function CreateSafeModal({
  open,
  onClose,
  onSuccess,
}: CreateSafeModalProps) {
  const chains = useChains()
  const { address: userAddress } = useWallet()
  const { colors } = useTheme()
  const { predictOperationalSafeAddress, deployOperationalSafe } = useNewSafe()
  const { showSuccess, showError } = useNotification()

  const [currentStep, setCurrentStep] = useState(0)
  const [safeName, setSafeName] = useState('')
  const [randomName] = useState(() => getRandomSafeName())
  const [selectedChains, setSelectedChains] = useState<Chain[]>([])
  const [signers, setSigners] = useState<string[]>(['', ''])
  const [threshold, setThreshold] = useState<number>(MIN_SAFE_THRESHOLD)
  const [thresholdInput, setThresholdInput] = useState<string>(
    MIN_SAFE_THRESHOLD.toString()
  )
  const [thresholdError, setThresholdError] = useState<string>('')
  const [saltNonce, setSaltNonce] = useState<number>(0)
  const [saltNonceInput, setSaltNonceInput] = useState<string>('0')
  const [saltNonceError, setSaltNonceError] = useState<string>('')
  const [useAutoNonce, setUseAutoNonce] = useState<boolean>(true)
  const [isPredicting, setIsPredicting] = useState(false)
  const [predictError, setPredictError] = useState<string | null>(null)
  const [predictedAddresses, setPredictedAddresses] = useState<
    Record<string, `0x${string}` | null>
  >({})
  const [isDeploying, setIsDeploying] = useState(false)
  const [deploySteps, setDeploySteps] =
    useState<SafeDeployStep[]>(DEFAULT_DEPLOY_STEPS)
  const [deployError, setDeployError] = useState<string | null>(null)
  const [deployTxHash, setDeployTxHash] = useState<string | null>(null)
  const [showDeploymentProgress, setShowDeploymentProgress] = useState(false)
  const [showCancelledModal, setShowCancelledModal] = useState(false)
  const [walletAddress, setWalletAddress] = useState<string | null>(null)
  const [safeId, setSafeId] = useState<string | number | null>(null)
  const [isLoadingApi, setIsLoadingApi] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  useEffect(() => {
    if (threshold > 0 && threshold > signers.length && signers.length > 0) {
      setThresholdError(
        `Threshold cannot exceed the number of signers (${signers.length})`
      )
    } else if (threshold < MIN_SAFE_THRESHOLD && threshold > 0) {
      setThresholdError('Threshold must be at least 2')
    } else if (
      thresholdError &&
      threshold <= signers.length &&
      threshold >= MIN_SAFE_THRESHOLD
    ) {
      setThresholdError('')
    }
  }, [signers.length, threshold, thresholdError])

  const predictConsistentSafeAddressAcrossChains = useCallback(
    async (
      owners: `0x${string}`[],
      threshold: number,
      chains: Chain[],
      initialSaltNonce: string,
      maxAttempts = 20
    ) => {
      let saltNonce = initialSaltNonce
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const predictions: Record<
          string,
          { address: `0x${string}`; isDeployed: boolean }
        > = {}
        for (const chain of chains) {
          let result: { address: `0x${string}`; isDeployed: boolean } = {
            address: zeroAddress as `0x${string}`,
            isDeployed: false,
          }
          try {
            result = await predictOperationalSafeAddress(
              owners,
              threshold,
              chain,
              parseInt(saltNonce)
            )
          } catch {
            // fallback already set
          }
          predictions[chain.id] = result
        }
        const addresses = Object.values(predictions).map((p) => p.address)
        const allMatch = addresses.every((addr) => addr === addresses[0])
        const anyDeployed = Object.values(predictions).some((p) => p.isDeployed)

        if (allMatch && !anyDeployed) {
          return { safeAddress: addresses[0], saltNonce, predictions }
        }
        saltNonce = (parseInt(saltNonce) + 1).toString()
      }
      throw new Error('Could not find consistent Safe address across chains')
    },
    [predictOperationalSafeAddress]
  )

  useEffect(() => {
    let cancelled = false
    async function runPrediction() {
      if (
        currentStep !== 2 ||
        selectedChains.length === 0 ||
        signers.filter(Boolean).length === 0 ||
        threshold === 0
      )
        return

      setIsPredicting(true)
      setPredictError(null)
      const validSigners = signers.filter(isValidAddress)
      try {
        const initialNonce = useAutoNonce ? '0' : saltNonceInput
        const { saltNonce: foundSaltNonce, predictions } =
          await predictConsistentSafeAddressAcrossChains(
            validSigners,
            threshold,
            selectedChains,
            initialNonce
          )
        if (!cancelled) {
          setPredictedAddresses(
            Object.fromEntries(
              Object.entries(predictions).map(([id, prediction]) => [
                id,
                prediction.address,
              ])
            )
          )
          if (useAutoNonce) {
            setSaltNonce(Number(foundSaltNonce))
            setSaltNonceInput(foundSaltNonce)
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const errorMessage =
            typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message?: unknown }).message)
              : 'Prediction error'
          setPredictError(errorMessage)
          setPredictedAddresses({})
        }
      } finally {
        if (!cancelled) setIsPredicting(false)
      }
    }
    runPrediction()
    return () => {
      cancelled = true
    }
  }, [
    currentStep,
    selectedChains,
    signers,
    threshold,
    saltNonceInput,
    useAutoNonce,
    predictConsistentSafeAddressAcrossChains,
  ])

  const handleApiError = useCallback(
    async (response: Response, defaultMessage: string): Promise<never> => {
      const errorText = await response.text()
      let errorMessage = errorText
      try {
        const errorJson = JSON.parse(errorText)
        if (errorJson.message) {
          errorMessage = errorJson.message
        } else if (errorJson.error) {
          errorMessage = errorJson.error
        } else {
          errorMessage = errorText || defaultMessage
        }
      } catch {
        if (errorText) {
          errorMessage = errorText
        } else {
          errorMessage = defaultMessage
        }
      }
      throw new Error(errorMessage)
    },
    []
  )

  const createSafeOnApi = useCallback(
    async (chainId: number, name: string, initiatedBy: string) => {
      const env = getEnv()
      if (!env.apiUrl) {
        throw new Error('API URL is not configured')
      }

      const response = await fetch(`${env.apiUrl}/v1/admin/safes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${env.adminBearerToken}`,
        },
        body: JSON.stringify({
          chainId,
          name,
          initiatedBy,
        }),
      })

      if (!response.ok) {
        await handleApiError(response, 'Failed to create safe')
      }

      return response.json()
    },
    [handleApiError]
  )

  const updateSafeOnApi = useCallback(
    async (id: string | number, name: string, safeAddress: string) => {
      const env = getEnv()
      if (!env.apiUrl) {
        throw new Error('API URL is not configured')
      }

      const response = await fetch(`${env.apiUrl}/v1/admin/safes/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${env.adminBearerToken}`,
        },
        body: JSON.stringify({
          name,
          safeAddress,
        }),
      })

      if (!response.ok) {
        await handleApiError(response, 'Failed to update safe')
      }

      return response.json()
    },
    [handleApiError]
  )

  const toggleChain = (chain: Chain) => {
    const exists = selectedChains.some((c) => c.id === chain.id)
    setSelectedChains(
      exists
        ? selectedChains.filter((c) => c.id !== chain.id)
        : [...selectedChains, chain]
    )
  }

  const addSignerField = () => {
    setSigners((prev) => {
      if (prev.length < MIN_SAFE_THRESHOLD) {
        return [...prev, '']
      }
      return [...prev, '']
    })
  }

  const removeSignerField = (signerIdx: number) => {
    if (signerIdx === 0 && walletAddress !== null) {
      return
    }
    if (signerIdx === 1 && signers.length === 2) {
      return
    }
    setSigners((prev) => prev.filter((_, idx) => idx !== signerIdx))
  }

  const handleSignerChange = (signerIdx: number, value: string) => {
    setSigners((prevSigners) =>
      prevSigners.map((owner, idx) => (idx === signerIdx ? value : owner))
    )
  }

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    if (inputValue === '') {
      setThresholdInput('')
      setThreshold(0)
      setThresholdError('Threshold is required')
      return
    }

    const digitsOnly = inputValue.replace(/\D/g, '')

    if (digitsOnly === '') {
      setThresholdInput('')
      setThreshold(0)
      setThresholdError('Threshold is required')
      return
    }

    const val = digitsOnly.replace(/^0+/, '') || '0'

    const numValue = Number(val)

    if (numValue === 0) {
      setThresholdInput(val)
      setThreshold(0)
      setThresholdError('Threshold must be greater than 0')
      return
    }

    if (numValue < MIN_SAFE_THRESHOLD) {
      setThresholdInput(val)
      setThreshold(numValue)
      setThresholdError('Threshold must be at least 2')
      return
    }

    if (numValue > signers.length) {
      setThresholdInput(val)
      setThreshold(numValue)
      setThresholdError(
        `Threshold cannot exceed the number of signers (${signers.length})`
      )
      return
    }

    setThresholdInput(val)
    setThreshold(numValue)
    setThresholdError('')
  }

  const handleSaltNonceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    if (inputValue === '') {
      setSaltNonceInput('')
      setSaltNonce(0)
      setSaltNonceError('')
      return
    }

    const digitsOnly = inputValue.replace(/\D/g, '')

    if (digitsOnly === '') {
      setSaltNonceInput('')
      setSaltNonce(0)
      setSaltNonceError('')
      return
    }

    const val = digitsOnly.replace(/^0+/, '') || '0'
    const numValue = Number(val)

    if (numValue > Number.MAX_SAFE_INTEGER) {
      setSaltNonceError('Nonce value is too large')
      return
    }

    setSaltNonceInput(val)
    setSaltNonce(numValue)
    setSaltNonceError('')
    setUseAutoNonce(false) // User manually set nonce, disable auto
  }

  const handleNext = async () => {
    if (currentStep === 0) {
      if (!safeName.trim()) {
        setApiError('Safe name is required')
        showError('Safe name is required')
        return
      }
      if (selectedChains.length === 0) {
        setApiError('Please select at least one network')
        showError('Please select at least one network')
        return
      }
      if (!userAddress) {
        setApiError('Please connect your wallet')
        showError('Please connect your wallet')
        return
      }

      setIsLoadingApi(true)
      setApiError(null)
      try {
        const response = await createSafeOnApi(
          selectedChains[0].id,
          safeName.trim(),
          userAddress
        )
        setWalletAddress(response.walletAddress)
        setSafeId(response.id || response._id || response.safeId)
        setSigners([response.walletAddress, ''])
        setCurrentStep(1)
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create safe'
        setApiError(errorMessage)
        showError(errorMessage)
      } finally {
        setIsLoadingApi(false)
      }
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleDeploySafe = async () => {
    setShowDeploymentProgress(true)
    setIsDeploying(true)
    setDeployError(null)
    setDeploySteps(DEFAULT_DEPLOY_STEPS.map((step) => ({ ...step })))
    setDeployTxHash(null)
    try {
      const predictedAddress = Object.values(predictedAddresses).find(
        (addr) => !!addr
      )
      if (!predictedAddress) {
        throw new Error('Predicted address not found')
      }

      if (!safeId) {
        throw new Error('Safe ID not found')
      }

      try {
        await updateSafeOnApi(
          safeId,
          safeName.trim() || randomName,
          predictedAddress
        )
      } catch (updateError) {
        const errorMessage =
          updateError instanceof Error
            ? updateError.message
            : 'Failed to update safe before deployment'
        setDeployError(`Failed to update safe: ${errorMessage}`)
        showError(errorMessage)
        setIsDeploying(false)
        setShowDeploymentProgress(false)
        return
      }

      const validSigners = signers.filter(isValidAddress) as `0x${string}`[]
      const steps = await deployOperationalSafe(
        validSigners,
        threshold,
        selectedChains[0],
        saltNonce,
        safeName.trim() || randomName,
        setDeploySteps
      )
      setDeploySteps([...steps])
      const txStep = steps.find((s) => s.txHash)
      if (txStep && txStep.txHash) {
        setDeployTxHash(txStep.txHash)
      }
      if (steps.some((s) => s.status === 'error')) {
        const errorStep = steps.find((s) => s.status === 'error')
        const errorMessage = errorStep && errorStep.error ? errorStep.error : ''

        if (isUserRejectedError(errorMessage)) {
          setShowCancelledModal(true)
          setShowDeploymentProgress(false)
        } else {
          setDeployError(
            errorMessage
              ? `Deployment error: ${errorMessage}`
              : 'Deployment error'
          )
        }
        return
      }
      showSuccess('Operational Safe Account created successfully!')
      handleClose()
      onSuccess?.()
    } catch (e: unknown) {
      const errorMessage =
        typeof e === 'object' && e !== null && 'message' in e
          ? String((e as { message?: unknown }).message)
          : 'Unexpected deployment error'

      if (isUserRejectedError(errorMessage)) {
        setShowCancelledModal(true)
        setShowDeploymentProgress(false)
      } else {
        setDeployError(errorMessage)
        showError(errorMessage)
      }
    } finally {
      setIsDeploying(false)
      setSaltNonce(0)
    }
  }

  const handleClose = () => {
    setCurrentStep(0)
    setSafeName('')
    setSelectedChains([])
    setSigners(['', ''])
    setThreshold(MIN_SAFE_THRESHOLD)
    setThresholdInput(MIN_SAFE_THRESHOLD.toString())
    setThresholdError('')
    setSaltNonce(0)
    setSaltNonceInput('0')
    setSaltNonceError('')
    setUseAutoNonce(true)
    setPredictError(null)
    setPredictedAddresses({})
    setDeployError(null)
    setDeployTxHash(null)
    setShowDeploymentProgress(false)
    setShowCancelledModal(false)
    setWalletAddress(null)
    setSafeId(null)
    setApiError(null)
    setIsLoadingApi(false)
    onClose()
  }

  const addressPattern = /^0x[a-fA-F0-9]{40}$/
  const allSignersValid =
    signers.length >= MIN_SAFE_THRESHOLD &&
    signers.every((addr) => addr.trim() && addressPattern.test(addr))
  const lowerSigners = signers.map((addr) => addr.toLowerCase())
  const duplicateIndexes = lowerSigners
    .map((addr, idx, arr) => (arr.indexOf(addr) !== idx ? idx : -1))
    .filter((idx) => idx !== -1)
  const hasDuplicates = duplicateIndexes.length > 0

  const getInputError = (value: string, idx: number) => {
    if (!value) {
      if (idx === 1) return true
      return false
    }
    const isInvalid = !addressPattern.test(value)
    const isDuplicate = duplicateIndexes.includes(idx)
    return isInvalid || isDuplicate
  }

  const getHelperText = (
    value: string,
    idx: number,
    isOwner1: boolean,
    isOwner2: boolean
  ): string => {
    if (getInputError(value, idx)) {
      return 'Invalid address or duplicate'
    }
    if (isOwner1) {
      return "This address is set automatically. You can't change it."
    }
    if (isOwner2 && !value.trim()) {
      return 'Owner 2 is required'
    }
    return ''
  }

  const canProceedFromStep0 =
    selectedChains.length > 0 && safeName.trim().length > 0

  const canProceedFromStep1 =
    signers.length >= MIN_SAFE_THRESHOLD &&
    allSignersValid &&
    !hasDuplicates &&
    threshold >= MIN_SAFE_THRESHOLD &&
    threshold <= signers.length &&
    !thresholdError &&
    signers[1]?.trim().length > 0

  const canDeploy =
    Object.values(predictedAddresses).some((addr) => !!addr) &&
    !isPredicting &&
    !isDeploying

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.background,
          border: `1px solid ${colors.primary}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: colors.yellow,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
        }}
      >
        Create New Safe Account
        <IconButton onClick={handleClose} sx={{ color: colors.text }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stepper
          activeStep={currentStep}
          sx={{
            mb: 4,
            mt: 2,
            '& .MuiStepIcon-text': {
              fontFamily: 'inherit',
              fontSize: 'inherit',
              fontWeight: 'inherit',
            },
            '& .MuiStepIcon-root.Mui-active': {
              color: `${colors.primary} !important`,
            },
            '& .MuiStepIcon-root.Mui-active .MuiStepIcon-circle': {
              fill: 'currentColor',
            },
            '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
              fill: `${colors.background} !important`,
              fontFamily: 'inherit',
              fontSize: 'inherit',
              fontWeight: 'inherit',
            },
            '& .MuiStepIcon-root:not(.Mui-active):not(.Mui-completed)': {
              '& .MuiStepIcon-circle': {
                fill: 'transparent',
                stroke: colors.primary,
                strokeWidth: 2,
              },
              '& .MuiStepIcon-text': {
                fill: colors.primary,
                fontFamily: 'inherit',
                fontSize: 'inherit',
                fontWeight: 'inherit',
              },
            },
            '& .MuiStepLabel-label': {
              color: `${colors.yellow} !important`,
            },
            '& .MuiStepLabel-root.Mui-active .MuiStepLabel-label': {
              color: `${colors.yellow} !important`,
            },
            '& .MuiStepLabel-root.Mui-completed .MuiStepLabel-label': {
              color: `${colors.yellow} !important`,
            },
          }}
        >
          <Step>
            <StepLabel>Safe Name</StepLabel>
          </Step>
          <Step>
            <StepLabel>Signers and Threshold</StepLabel>
          </Step>
          <Step>
            <StepLabel>Validate</StepLabel>
          </Step>
        </Stepper>

        {showDeploymentProgress && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: colors.text }}>
              Deployment Progress
            </Typography>
            {deploySteps.map((step, idx) => (
              <Box key={idx} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: colors.text }}>
                    {
                      STEPS_DEPLOY_LABEL[
                        step.step as keyof typeof STEPS_DEPLOY_LABEL
                      ]
                    }
                  </Typography>
                  {step.status === 'success' && (
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      ✓
                    </Typography>
                  )}
                  {step.status === 'error' && (
                    <Typography variant="body2" sx={{ color: 'error.main' }}>
                      ✗
                    </Typography>
                  )}
                  {step.status === 'running' && (
                    <LinearProgress sx={{ width: 100 }} />
                  )}
                </Box>
                {step.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {step.error}
                  </Alert>
                )}
              </Box>
            ))}
            {deployTxHash && (
              <Typography variant="body2" sx={{ color: colors.text, mt: 2 }}>
                Transaction Hash: {deployTxHash}
              </Typography>
            )}
            {deployError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deployError}
              </Alert>
            )}
          </Box>
        )}

        {!showDeploymentProgress && deployError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {deployError}
          </Alert>
        )}

        {currentStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Safe Name"
              value={safeName}
              onChange={(e) => setSafeName(sanitizeUserInput(e.target.value))}
              placeholder="Enter safe name"
              fullWidth
              required
              autoComplete="off"
              error={!safeName.trim()}
              helperText={
                !safeName.trim()
                  ? 'Safe name is required'
                  : apiError || 'Enter a name for your safe'
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: colors.text,
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.primary },
                },
                '& .MuiInputLabel-root': { color: colors.text },
              }}
            />
            {apiError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {apiError}
              </Alert>
            )}

            <Box>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, color: colors.text }}
              >
                Select Networks
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label="Clear"
                  onClick={() => setSelectedChains([])}
                  sx={{
                    backgroundColor: colors.primary + '33',
                    color: colors.text,
                    '&:hover': { backgroundColor: colors.primary + '55' },
                  }}
                />
                {chains.map((chainObj) => {
                  const selected = selectedChains.some(
                    (c) => c.id === chainObj.id
                  )
                  return (
                    <Chip
                      key={chainObj.id}
                      label={chainObj.name}
                      onClick={() => toggleChain(chainObj)}
                      sx={{
                        backgroundColor: selected
                          ? colors.primary
                          : colors.primary + '33',
                        color: selected ? colors.background : colors.text,
                        '&:hover': {
                          backgroundColor: selected
                            ? colors.primary
                            : colors.primary + '55',
                        },
                      }}
                    />
                  )
                })}
              </Box>
            </Box>
          </Box>
        )}

        {currentStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="subtitle1" sx={{ color: colors.text }}>
              Signers and Threshold
            </Typography>
            {signers.map((owner, idx) => {
              const isOwner1 = idx === 0 && walletAddress !== null
              const isOwner2 = idx === 1
              return (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label={`Owner ${idx + 1}${isOwner2 ? ' *' : ''}`}
                    value={owner}
                    onChange={(e) => handleSignerChange(idx, e.target.value)}
                    placeholder="0x..."
                    fullWidth
                    required={isOwner2}
                    disabled={isOwner1}
                    error={
                      getInputError(owner, idx) || (isOwner2 && !owner.trim())
                    }
                    helperText={getHelperText(owner, idx, isOwner1, isOwner2)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: colors.text,
                        '& fieldset': { borderColor: colors.primary },
                        '&.Mui-disabled': {
                          backgroundColor: colors.overlay + '33',
                          color: `${colors.text} !important`,
                          '& input': {
                            color: `${colors.text} !important`,
                            WebkitTextFillColor: `${colors.text} !important`,
                          },
                        },
                      },
                      '& .MuiInputLabel-root': { color: colors.text },
                      '& .MuiFormHelperText-root': {
                        color: colors.text,
                      },
                    }}
                  />
                  {signers.length > 2 && !isOwner1 && (
                    <IconButton
                      onClick={() => removeSignerField(idx)}
                      sx={{
                        color: colors.purple,
                        minWidth: 'auto',
                        width: 56,
                        height: 56,
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
              )
            })}
            <Button
              startIcon={<AddIcon />}
              onClick={addSignerField}
              sx={{
                color: colors.primary,
                borderColor: colors.primary,
                border: '1px solid',
                alignSelf: 'flex-start',
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}22`,
                },
              }}
            >
              Add Owner
            </Button>
            <Box>
              <TextField
                label="Threshold"
                type="text"
                value={thresholdInput}
                onChange={handleThresholdChange}
                error={!!thresholdError}
                helperText={
                  thresholdError ||
                  `${threshold || 0} out of ${
                    signers.length
                  } signers required to confirm a transaction (minimum 2)`
                }
                fullWidth
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: colors.text,
                    '& fieldset': { borderColor: colors.primary },
                    // Hide spinner buttons
                    '& input[type="text"]::-webkit-outer-spin-button, & input[type="text"]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    '& input[type="text"]': {
                      MozAppearance: 'textfield',
                    },
                  },
                  '& .MuiInputLabel-root': { color: colors.text },
                  '& .MuiFormHelperText-root': {
                    color: thresholdError ? colors.error : colors.text,
                  },
                }}
              />
            </Box>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={useAutoNonce}
                  onChange={(e) => {
                    setUseAutoNonce(e.target.checked)
                    if (e.target.checked) {
                      setSaltNonceInput('0')
                      setSaltNonce(0)
                    }
                  }}
                  style={{
                    accentColor: colors.primary,
                    cursor: 'pointer',
                  }}
                />
                <Typography variant="body2" sx={{ color: colors.text }}>
                  Auto-generate nonce (recommended)
                </Typography>
              </Box>
              <TextField
                label="Salt Nonce"
                type="text"
                value={saltNonceInput}
                onChange={handleSaltNonceChange}
                disabled={useAutoNonce}
                error={!!saltNonceError}
                helperText={
                  saltNonceError ||
                  (useAutoNonce
                    ? 'Nonce will be automatically found to ensure unique address'
                    : 'Manual nonce value (0 by default)')
                }
                fullWidth
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: colors.text,
                    '& fieldset': { borderColor: colors.primary },
                    '&.Mui-disabled': {
                      backgroundColor: colors.overlay + '33',
                    },
                    '& input[type="text"]::-webkit-outer-spin-button, & input[type="text"]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    '& input[type="text"]': {
                      MozAppearance: 'textfield',
                    },
                  },
                  '& .MuiInputLabel-root': { color: colors.text },
                  '& .MuiFormHelperText-root': {
                    color: saltNonceError ? colors.error : colors.text,
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {currentStep === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: colors.overlay,
                border: `1px solid ${colors.primary}33`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, color: colors.text }}
              >
                Safe Name: {safeName.trim() || randomName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: colors.text }}>
                Networks: {selectedChains.map((c) => c.name).join(', ')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: colors.text }}>
                Signers: {signers.filter(Boolean).length}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: colors.text }}>
                Threshold: {threshold} / {signers.filter(Boolean).length}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text }}>
                Salt Nonce: {saltNonce} {useAutoNonce && '(auto-generated)'}
              </Typography>
            </Paper>
            {isPredicting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: colors.text }}>
                  Predicting Safe address
                </Typography>
                <LinearProgress sx={{ flex: 1 }} />
              </Box>
            ) : (
              Object.keys(predictedAddresses).length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, color: colors.text }}
                  >
                    Predicted Safe Address:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 1,
                      backgroundColor: colors.overlay,
                      border: `1px solid ${colors.primary}33`,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      color: colors.primary,
                    }}
                  >
                    {Object.values(predictedAddresses).find((addr) => !!addr) ||
                      'N/A'}
                  </Typography>
                </Box>
              )
            )}
            {predictError && <Alert severity="error">{predictError}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {currentStep > 0 && (
          <Button
            onClick={() => setCurrentStep(currentStep - 1)}
            sx={{ color: colors.text }}
          >
            Back
          </Button>
        )}
        {currentStep < 2 && (
          <Button
            onClick={handleNext}
            disabled={
              (currentStep === 0 && (!canProceedFromStep0 || isLoadingApi)) ||
              (currentStep === 1 && !canProceedFromStep1) ||
              isLoadingApi
            }
            sx={{
              color: colors.primary,
              borderColor: colors.primary,
              border: '1px solid',
            }}
          >
            {isLoadingApi ? 'Loading...' : 'Next'}
          </Button>
        )}
        {currentStep === 2 && (
          <Button
            onClick={handleDeploySafe}
            disabled={!canDeploy}
            variant="contained"
            sx={{
              backgroundColor: colors.primary,
              color: colors.overlay,
              '&:hover': { backgroundColor: colors.primary },
            }}
          >
            {isDeploying ? 'Deploying...' : 'Deploy New Safe'}
          </Button>
        )}
      </DialogActions>
      <TransactionCancelledModal
        open={showCancelledModal}
        onClose={() => {
          setShowCancelledModal(false)
          handleClose()
        }}
        message="You cancelled the Safe creation transaction in MetaMask."
      />
    </Dialog>
  )
}
